import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { getPublicSiteUrl } from '@/lib/seo';
import { buildPathForLocale, canonicalHomeUrl, getLocaleFromPathname } from '@/lib/intl-seo';
import { PageBreadcrumb } from '@/components/seo/PageBreadcrumb';
import { breadcrumbListJsonLd, articleJsonLd, jsonLdScript } from '@/lib/schema-org';
import { 
  BookOpen, Store, Upload, Package, Search, Settings, 
  ChevronRight, Play, CheckCircle, ArrowRight, FileSpreadsheet,
  Link, Database, ShoppingCart, Tag, Truck, Globe, Volume2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';

// Video Player with Language-based Audio Component
const VideoWithNarration = ({ videoSrc, videoNum, colorClass }) => {
  const { i18n, t } = useTranslation();
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const currentLang = i18n.language?.substring(0, 2) || 'en';
  const audioSrc = `/audio/narration-${videoNum}-${currentLang}.mp3`;

  // Sync audio with video
  useEffect(() => {
    const video = videoRef.current;
    const audio = audioRef.current;
    
    if (!video || !audio) return;

    const handlePlay = () => {
      audio.currentTime = video.currentTime;
      audio.play().catch(() => {});
      setIsPlaying(true);
    };

    const handlePause = () => {
      audio.pause();
      setIsPlaying(false);
    };

    const handleSeeked = () => {
      audio.currentTime = video.currentTime;
    };

    const handleEnded = () => {
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('ended', handleEnded);
    };
  }, []);

  // Update audio source when language changes
  useEffect(() => {
    const audio = audioRef.current;
    const video = videoRef.current;
    
    if (audio && video) {
      const wasPlaying = !video.paused;
      audio.src = audioSrc;
      audio.load();
      
      if (wasPlaying) {
        audio.currentTime = video.currentTime;
        audio.play().catch(() => {});
      }
    }
  }, [audioSrc]);

  return (
    <div className="relative">
      <div className="aspect-video bg-black rounded-b-lg overflow-hidden">
        <video 
          ref={videoRef}
          className="w-full h-full object-cover" 
          controls
          muted
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
        <audio ref={audioRef} src={audioSrc} preload="auto" />
      </div>
      {isPlaying && (
        <div className={`absolute top-2 right-2 ${colorClass} text-white px-2 py-1 rounded-full text-xs flex items-center gap-1`}>
          <Volume2 className="w-3 h-3" />
          {currentLang.toUpperCase()}
        </div>
      )}
    </div>
  );
};

const SellerGuidePage = () => {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const location = useLocation();
  const siteBase = getPublicSiteUrl();
  const [activeStep, setActiveStep] = useState(1);

  const steps = [
    {
      id: 1,
      icon: Settings,
      titleKey: 'sellerGuide.step1.title',
      descKey: 'sellerGuide.step1.description',
      details: [
        'sellerGuide.step1.detail1',
        'sellerGuide.step1.detail2',
        'sellerGuide.step1.detail3',
        'sellerGuide.step1.detail4',
        'sellerGuide.step1.detail5'
      ]
    },
    {
      id: 2,
      icon: Search,
      titleKey: 'sellerGuide.step2.title',
      descKey: 'sellerGuide.step2.description',
      details: [
        'sellerGuide.step2.detail1',
        'sellerGuide.step2.detail2',
        'sellerGuide.step2.detail3',
        'sellerGuide.step2.detail4'
      ]
    },
    {
      id: 3,
      icon: Upload,
      titleKey: 'sellerGuide.step3.title',
      descKey: 'sellerGuide.step3.description',
      details: [
        'sellerGuide.step3.detail1',
        'sellerGuide.step3.detail2',
        'sellerGuide.step3.detail3',
        'sellerGuide.step3.detail4'
      ]
    },
    {
      id: 4,
      icon: Package,
      titleKey: 'sellerGuide.step4.title',
      descKey: 'sellerGuide.step4.description',
      details: [
        'sellerGuide.step4.detail1',
        'sellerGuide.step4.detail2',
        'sellerGuide.step4.detail3',
        'sellerGuide.step4.detail4'
      ]
    },
    {
      id: 5,
      icon: ShoppingCart,
      titleKey: 'sellerGuide.step5.title',
      descKey: 'sellerGuide.step5.description',
      details: [
        'sellerGuide.step5.detail1',
        'sellerGuide.step5.detail2',
        'sellerGuide.step5.detail3'
      ]
    }
  ];

  const importMethods = [
    {
      icon: FileSpreadsheet,
      titleKey: 'sellerGuide.import.csv.title',
      descKey: 'sellerGuide.import.csv.description',
      steps: [
        'sellerGuide.import.csv.step1',
        'sellerGuide.import.csv.step2',
        'sellerGuide.import.csv.step3',
        'sellerGuide.import.csv.step4'
      ]
    },
    {
      icon: Link,
      titleKey: 'sellerGuide.import.url.title',
      descKey: 'sellerGuide.import.url.description',
      steps: [
        'sellerGuide.import.url.step1',
        'sellerGuide.import.url.step2',
        'sellerGuide.import.url.step3',
        'sellerGuide.import.url.step4'
      ]
    },
    {
      icon: Database,
      titleKey: 'sellerGuide.import.api.title',
      descKey: 'sellerGuide.import.api.description',
      steps: [
        'sellerGuide.import.api.step1',
        'sellerGuide.import.api.step2',
        'sellerGuide.import.api.step3',
        'sellerGuide.import.api.step4'
      ]
    }
  ];

  const tips = [
    'sellerGuide.tips.tip1',
    'sellerGuide.tips.tip2',
    'sellerGuide.tips.tip3',
    'sellerGuide.tips.tip4',
    'sellerGuide.tips.tip5'
  ];

  const langCode =
    lang || getLocaleFromPathname(location.pathname) || (i18n.language || 'en').substring(0, 2);
  const guideDesc = t(
    'seo.sellerGuideDescription',
    'Satıcılar için ürün yükleme, XML ve mağaza yönetimi rehberi — Turkish E-Market.',
  );
  const canonicalGuide = siteBase
    ? `${siteBase.replace(/\/$/, '')}${buildPathForLocale(langCode, ['seller-guide'])}`
    : '';

  const sellerBreadcrumbLd = useMemo(() => {
    if (!siteBase || !canonicalGuide) return '';
    return jsonLdScript(
      breadcrumbListJsonLd([
        { name: t('common.home', 'Ana Sayfa'), item: canonicalHomeUrl(siteBase, langCode) },
        { name: t('sellerGuide.title'), item: canonicalGuide },
      ]),
    );
  }, [siteBase, canonicalGuide, langCode, t]);

  const sellerArticleLd = useMemo(() => {
    if (!siteBase || !canonicalGuide) return '';
    return jsonLdScript(
      articleJsonLd({
        url: canonicalGuide,
        headline: t('sellerGuide.title'),
        description: guideDesc,
        site: siteBase,
        langCode,
      }),
    );
  }, [siteBase, canonicalGuide, guideDesc, langCode, t]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white" data-seo-ready="true">
      <Helmet htmlAttributes={{ lang: langCode }}>
        <title>{`${t('sellerGuide.title')} | Turkish E-Market`}</title>
        <meta name="description" content={guideDesc} />
        {canonicalGuide ? <link rel="canonical" href={canonicalGuide} /> : null}
        {sellerBreadcrumbLd ? (
          <script type="application/ld+json">{sellerBreadcrumbLd}</script>
        ) : null}
        {sellerArticleLd ? <script type="application/ld+json">{sellerArticleLd}</script> : null}
      </Helmet>
      {/* Header */}
      <div className="bg-emerald-600 text-white py-12">
        <div className="container mx-auto px-4">
          <PageBreadcrumb
            className="!text-emerald-100 mb-4 [&_a]:text-emerald-100 [&_a:hover]:text-white [&_span]:text-white"
            items={[
              { label: t('common.home', 'Ana Sayfa'), to: buildPathForLocale(langCode, []) },
              { label: t('sellerGuide.title'), to: null },
            ]}
          />
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="w-10 h-10" />
            <h1 className="text-3xl md:text-4xl font-bold">{t('sellerGuide.title')}</h1>
          </div>
          <p className="text-emerald-100 text-lg max-w-2xl">
            {t('sellerGuide.subtitle')}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Language Audio Info Banner */}
        <div className="bg-gradient-to-r from-emerald-100 to-blue-100 border border-emerald-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <Volume2 className="w-6 h-6 text-emerald-600" />
          <div>
            <p className="font-medium text-emerald-800">{t('sellerGuide.audioInfo.title')}</p>
            <p className="text-sm text-emerald-600">{t('sellerGuide.audioInfo.description')}</p>
          </div>
        </div>

        {/* Video Sections - 4 Videos */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Video 1: Login & Dashboard */}
          <Card className="border-emerald-200 overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-emerald-700 text-lg">
                <Play className="w-4 h-4" />
                {t('sellerGuide.videos.video1.title')}
              </CardTitle>
              <CardDescription className="text-sm">{t('sellerGuide.videos.video1.description')}</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <VideoWithNarration 
                videoSrc="/seller-guide-video-1.mp4" 
                videoNum={1} 
                colorClass="bg-emerald-600"
              />
            </CardContent>
          </Card>

          {/* Video 2: Product Catalog */}
          <Card className="border-blue-200 overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-blue-700 text-lg">
                <Play className="w-4 h-4" />
                {t('sellerGuide.videos.video2.title')}
              </CardTitle>
              <CardDescription className="text-sm">{t('sellerGuide.videos.video2.description')}</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <VideoWithNarration 
                videoSrc="/seller-guide-video-2.mp4" 
                videoNum={2} 
                colorClass="bg-blue-600"
              />
            </CardContent>
          </Card>

          {/* Video 3: Order Management */}
          <Card className="border-orange-200 overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-orange-700 text-lg">
                <Play className="w-4 h-4" />
                {t('sellerGuide.videos.video3.title')}
              </CardTitle>
              <CardDescription className="text-sm">{t('sellerGuide.videos.video3.description')}</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <VideoWithNarration 
                videoSrc="/seller-guide-video-3.mp4" 
                videoNum={3} 
                colorClass="bg-orange-600"
              />
            </CardContent>
          </Card>

          {/* Video 4: Delivery */}
          <Card className="border-purple-200 overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-purple-700 text-lg">
                <Play className="w-4 h-4" />
                {t('sellerGuide.videos.video4.title')}
              </CardTitle>
              <CardDescription className="text-sm">{t('sellerGuide.videos.video4.description')}</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <VideoWithNarration 
                videoSrc="/seller-guide-video-4.mp4" 
                videoNum={4} 
                colorClass="bg-purple-600"
              />
            </CardContent>
          </Card>
        </div>

        {/* Quick Start Section */}
        <Card className="mb-8 border-emerald-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-700">
              <Play className="w-5 h-5" />
              {t('sellerGuide.quickStart.title')}
            </CardTitle>
            <CardDescription>{t('sellerGuide.quickStart.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {steps.map((step, index) => (
                <div 
                  key={step.id}
                  className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    activeStep === step.id 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-gray-200 hover:border-emerald-300'
                  }`}
                  onClick={() => setActiveStep(step.id)}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      activeStep === step.id ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {step.id}
                    </div>
                    <step.icon className={`w-5 h-5 ${activeStep === step.id ? 'text-emerald-600' : 'text-gray-400'}`} />
                  </div>
                  <h3 className="font-medium text-sm">{t(step.titleKey)}</h3>
                  {index < steps.length - 1 && (
                    <ChevronRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-300" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Step Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  {React.createElement(steps[activeStep - 1].icon, { className: "w-6 h-6 text-emerald-600" })}
                  <div>
                    <Badge variant="outline" className="mb-1">{t('sellerGuide.step')} {activeStep}</Badge>
                    <CardTitle>{t(steps[activeStep - 1].titleKey)}</CardTitle>
                  </div>
                </div>
                <CardDescription className="text-base mt-2">
                  {t(steps[activeStep - 1].descKey)}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {steps[activeStep - 1].details.map((detail, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>{t(detail)}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 flex gap-3">
                  {activeStep > 1 && (
                    <Button variant="outline" onClick={() => setActiveStep(activeStep - 1)}>
                      {t('sellerGuide.previousStep')}
                    </Button>
                  )}
                  {activeStep < steps.length && (
                    <Button onClick={() => setActiveStep(activeStep + 1)}>
                      {t('sellerGuide.nextStep')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                  {activeStep === steps.length && (
                    <Button asChild>
                      <a href="/seller">
                        {t('sellerGuide.goToDashboard')}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </a>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tips Sidebar */}
          <div>
            <Card className="bg-amber-50 border-amber-200">
              <CardHeader>
                <CardTitle className="text-amber-800 flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  {t('sellerGuide.tips.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {tips.map((tip, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-amber-900">
                      <span className="font-bold text-amber-600">{index + 1}.</span>
                      <span>{t(tip)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Import Methods Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-6 h-6 text-emerald-600" />
              {t('sellerGuide.importMethods.title')}
            </CardTitle>
            <CardDescription>{t('sellerGuide.importMethods.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="csv" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="csv" className="flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4" />
                  CSV / Excel
                </TabsTrigger>
                <TabsTrigger value="url" className="flex items-center gap-2">
                  <Link className="w-4 h-4" />
                  URL
                </TabsTrigger>
                <TabsTrigger value="api" className="flex items-center gap-2">
                  <Database className="w-4 h-4" />
                  API
                </TabsTrigger>
              </TabsList>

              {importMethods.map((method, methodIndex) => (
                <TabsContent key={methodIndex} value={['csv', 'url', 'api'][methodIndex]} className="mt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                        <method.icon className="w-5 h-5 text-emerald-600" />
                        {t(method.titleKey)}
                      </h3>
                      <p className="text-muted-foreground mb-4">{t(method.descKey)}</p>
                      <ol className="space-y-3">
                        {method.steps.map((step, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-medium flex-shrink-0">
                              {index + 1}
                            </div>
                            <span>{t(step)}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                    <div className="bg-gray-100 rounded-lg p-6 flex items-center justify-center">
                      <div className="text-center text-gray-500">
                        <method.icon className="w-16 h-16 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">{t('sellerGuide.importMethods.illustration')}</p>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>

        {/* Features Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="text-center p-6">
            <Store className="w-12 h-12 mx-auto mb-4 text-emerald-600" />
            <h3 className="font-semibold mb-2">{t('sellerGuide.features.store.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('sellerGuide.features.store.description')}</p>
          </Card>
          <Card className="text-center p-6">
            <Globe className="w-12 h-12 mx-auto mb-4 text-blue-600" />
            <h3 className="font-semibold mb-2">{t('sellerGuide.features.global.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('sellerGuide.features.global.description')}</p>
          </Card>
          <Card className="text-center p-6">
            <Truck className="w-12 h-12 mx-auto mb-4 text-orange-600" />
            <h3 className="font-semibold mb-2">{t('sellerGuide.features.delivery.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('sellerGuide.features.delivery.description')}</p>
          </Card>
        </div>

        {/* CTA */}
        <Card className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
          <CardContent className="py-8 text-center">
            <h2 className="text-2xl font-bold mb-3">{t('sellerGuide.cta.title')}</h2>
            <p className="text-emerald-100 mb-6 max-w-xl mx-auto">{t('sellerGuide.cta.description')}</p>
            <Button size="lg" variant="secondary" asChild>
              <a href="/seller">
                {t('sellerGuide.cta.button')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SellerGuidePage;
